import { useQuery, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  StatusCode,
  getCustomerSelf,
  type CustomerSelf as Customer,
} from '@hungryroot/rest';

export enum AuthState {
  isError,
  isLoading,
  isAuthenticated,
  isAnonymous,
}

interface AuthStateAuthenticated {
  authState: AuthState.isAuthenticated;
  customerData: Customer;
}

interface AuthStateAnonymous {
  authState: AuthState.isAnonymous;
  customerData?: never;
}

interface AuthStateError {
  authState: AuthState.isError;
  customerData?: never;
}

interface AuthStateLoading {
  authState: AuthState.isLoading;
  customerData?: Customer;
}

export type CustomerAuthState =
  | AuthStateAuthenticated
  | AuthStateAnonymous
  | AuthStateError
  | AuthStateLoading;

type Responses =
  | {
      status: StatusCode.SuccessOK;
      data: Customer;
    }
  | {
      status: StatusCode.ClientErrorForbidden;
      data?: never;
    }
  | {
      status: Exclude<
        StatusCode,
        StatusCode.SuccessOK | StatusCode.ClientErrorForbidden
      >;
      data?: never;
    };

function getAuthStateFromResponse(
  resp: Responses
): AuthStateAuthenticated | AuthStateAnonymous | AuthStateError {
  if (resp.status === StatusCode.SuccessOK) {
    return {
      authState: AuthState.isAuthenticated,
      customerData: resp.data,
    };
  }

  if (resp.status === StatusCode.ClientErrorForbidden) {
    return {
      authState: AuthState.isAnonymous,
    };
  }
  return {
    authState: AuthState.isError,
  };
}

// Note: Don't use this directly. Instead use `invalidateCustomerData`
// This is being exported for legacy implementations.
export const CUSTOMER_KEY = ['Customers', 'self', 'useCustomer'];

export async function invalidateCustomerData(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: CUSTOMER_KEY,
  });

  // Invalidates the `useCustomerSelf` cache key as well
  queryClient.invalidateQueries({
    queryKey: ['Customers', 'self'],
  });
}

function useCustomer() {
  return useQuery({
    // We are trying to reuse as much of the existing "useCustomerSelf" cache key
    // as possible. However, useCustomerSelf throws an error as a STRING when it gets a 403.
    // In this hook, we throw the response itself. This leads to downstream differences
    // and possible cache data inconsistencies when both hooks are present.

    // The tradeoff here is that we over request user data for screens where both hooks are used.
    queryKey: CUSTOMER_KEY,
    queryFn: async () => {
      const resp = await getCustomerSelf();

      if (resp.status !== 200 && resp.status !== 403) {
        throw resp;
      }
      return resp;
    },
    gcTime: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    retryOnMount: false,
    retry: false,
  });
}

const errorSchema = z.object({
  status: z.number(),
});

export function useIdentify(): CustomerAuthState {
  const { error, data } = useCustomer();
  if (data !== undefined) {
    return getAuthStateFromResponse(data);
  }

  if (error !== null) {
    const parsed = errorSchema.safeParse(error);
    if (parsed.success === true) {
      return getAuthStateFromResponse(parsed.data);
    }

    return {
      authState: AuthState.isError,
    };
  }

  return {
    authState: AuthState.isLoading,
    customerData: data,
  };
}
